import { Link } from "react-router-dom";
import type { TiebreakerNote } from "@mpl/shared";
import { pctLabel } from "../lib/format.js";
import { Diff, Num, Streak, WinLoss } from "./ui.js";
import DataTable, { type Column } from "./DataTable.js";

export interface StandingsRowView {
  managerId: number;
  name: string;
  slug: string | null;
  divisionName: string | null;
  wins: number;
  losses: number;
  ties: number;
  winPct: number;
  pointsFor: number;
  pointsAgainst: number;
  diff: number;
  divWins: number;
  divLosses: number;
  divTies: number;
  homeWins: number;
  homeLosses: number;
  awayWins: number;
  awayLosses: number;
  streak: string;
  strengthOfVictory: number;
  opponentWinPct: number;
  tiebreakerNote: TiebreakerNote | null;
  rank: number;
}

export default function StandingsTable({
  rows,
  showDivision = false,
  seeds = [],
  compact = false,
  power,
}: {
  rows: StandingsRowView[];
  showDivision?: boolean;
  /** manager ids that hold a playoff seed, in seed order */
  seeds?: number[];
  compact?: boolean;
  /** composite power rating by manager id; omit to leave the column out */
  power?: Map<number, number>;
}) {
  const seedOf = new Map(seeds.map((id, i) => [id, i + 1]));

  const columns: Column<StandingsRowView>[] = [
    {
      key: "rank",
      label: "#",
      compact: true,
      sortBy: (r) => r.rank,
      className: "text-chalk-500",
      render: (r) => {
        const seed = seedOf.get(r.managerId);
        return (
          <>
            {r.rank}
            {seed != null && (
              <span
                className="ml-1 text-[10px] font-bold text-blitz-400"
                title={`Playoff seed ${seed}`}
              >
                ●
              </span>
            )}
          </>
        );
      },
    },
    {
      key: "name",
      label: "Manager",
      compact: true,
      primary: true,
      sortBy: (r) => r.name,
      className: "font-semibold",
      render: (r) => (
        <>
          {r.slug ? (
            <Link to={`/managers/${r.slug}`} className="hover:text-blitz-400">
              {r.name}
            </Link>
          ) : (
            r.name
          )}
          {/* Hidden on a phone: the note is longer than the name it follows,
              and in the pinned column it pushes every actual number off the
              screen. The Tiebreakers card below says the same thing. */}
          {r.tiebreakerNote && (
            <span
              className="ml-2 hidden text-[11px] font-normal text-chalk-500 sm:inline"
              title={`Placed above ${r.tiebreakerNote.overName} on ${r.tiebreakerNote.criterion}`}
            >
              (won {r.tiebreakerNote.criterion} v {r.tiebreakerNote.overName})
            </span>
          )}
        </>
      ),
    },
    ...(showDivision
      ? [
          {
            key: "division",
            label: "Division",
            compact: true,
            sortBy: (r: StandingsRowView) => r.divisionName ?? "",
            className: "text-chalk-400",
            render: (r: StandingsRowView) => r.divisionName ?? "—",
          },
        ]
      : []),
    {
      key: "wins",
      label: "W-L",
      align: "right",
      compact: true,
      sortBy: (r) => r.winPct,
      render: (r) => <WinLoss w={r.wins} l={r.losses} t={r.ties} />,
    },
    {
      key: "pct",
      label: "Pct",
      align: "right",
      sortBy: (r) => r.winPct,
      render: (r) => <Num value={pctLabel(r.winPct)} muted />,
    },
    {
      key: "pf",
      label: "PF",
      align: "right",
      compact: true,
      sortBy: (r) => r.pointsFor,
      render: (r) => <Num value={r.pointsFor} />,
    },
    {
      key: "pa",
      label: "PA",
      align: "right",
      compact: true,
      sortBy: (r) => r.pointsAgainst,
      render: (r) => <Num value={r.pointsAgainst} />,
    },
    {
      key: "diff",
      label: "Diff",
      align: "right",
      compact: true,
      sortBy: (r) => r.diff,
      render: (r) => <Diff value={r.diff} />,
    },
    {
      key: "sov",
      label: "SOV",
      align: "right",
      title: "Strength of victory — mean win % of the managers you beat",
      sortBy: (r) => r.strengthOfVictory,
      render: (r) => <Num value={pctLabel(r.strengthOfVictory)} muted />,
    },
    {
      key: "sos",
      label: "SOS",
      align: "right",
      title: "Strength of schedule — mean win % of every manager you played",
      sortBy: (r) => r.opponentWinPct,
      render: (r) => <Num value={pctLabel(r.opponentWinPct)} muted />,
    },
    {
      key: "div",
      label: "Div",
      align: "right",
      sortBy: (r) => r.divWins - r.divLosses,
      render: (r) => (
        <WinLoss w={r.divWins} l={r.divLosses} t={r.divTies} className="text-chalk-400" />
      ),
    },
    {
      key: "home",
      label: "Home",
      align: "right",
      sortBy: (r) => r.homeWins - r.homeLosses,
      render: (r) => <WinLoss w={r.homeWins} l={r.homeLosses} className="text-chalk-400" />,
    },
    {
      key: "away",
      label: "Away",
      align: "right",
      sortBy: (r) => r.awayWins - r.awayLosses,
      render: (r) => <WinLoss w={r.awayWins} l={r.awayLosses} className="text-chalk-400" />,
    },
    ...(power
      ? [
          {
            key: "power",
            label: "PWR",
            align: "right" as const,
            compact: true,
            title:
              "Power rating out of 100 — record, point differential, schedule strength and divisional rank",
            sortBy: (r: StandingsRowView) => power.get(r.managerId) ?? -1,
            render: (r: StandingsRowView) => {
              const value = power.get(r.managerId);
              return value == null ? (
                <Num value="—" muted />
              ) : (
                <Num value={value.toFixed(1)} />
              );
            },
          },
        ]
      : []),
    {
      key: "streak",
      label: "Strk",
      align: "right",
      // "W3" ranks above "L1"; a dash sits between them.
      sortBy: (r) => {
        const n = Number(r.streak.slice(1)) || 0;
        return r.streak.startsWith("W") ? n : r.streak.startsWith("L") ? -n : 0;
      },
      render: (r) => <Streak value={r.streak} />,
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(r) => r.managerId}
      // Default order is the table's own ranking, so sorting is opt-in and the
      // standings still read as standings until clicked.
      initialSort={{ key: "rank", dir: "asc" }}
      compact={compact}
      minWidth={560}
    />
  );
}
